import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { WifiOff, RefreshCw, AlertTriangle } from 'lucide-react';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useOfflineSyncPanel } from '@/stores/offline-sync-panel.store';

/**
 * POS-I68: the bar used to float over the top of whatever page was open and hid its buttons (on the
 * till: Close, Shortcuts, store picker, Scan, Hold, Held, Switch Cashier, CFD, Receipt Preview).
 * It now takes its own row: while it shows, the page is pushed down by its height (see the
 * html[data-offline-banner] rules in index.css). The till (/pos) draws its own offline and sync
 * strips inside its layout, so the global bar stays out of its way there.
 */
const SELF_MANAGED_ROUTES = ['/pos'];

export function OfflineBanner() {
  const isOnline = useOnlineStatus();
  const { pending, failedCount } = useOfflineSync();
  const showPanel = useOfflineSyncPanel((s) => s.show);
  const { pathname } = useLocation();

  const hidden =
    (isOnline && pending === 0 && failedCount === 0) ||
    SELF_MANAGED_ROUTES.some((r) => pathname === r || pathname.startsWith(`${r}/`));

  useLayoutEffect(() => {
    const root = document.documentElement;
    if (hidden) root.removeAttribute('data-offline-banner');
    else root.setAttribute('data-offline-banner', 'on');
    return () => root.removeAttribute('data-offline-banner');
  }, [hidden]);

  // Nothing to say: online and nothing waiting or refused (or the page shows it itself).
  if (hidden) return null;

  // POS-I65: "Syncing…" only while sales are really waiting to be sent. A sale the server refused
  // is not syncing; it gets its own message with a way to the sync panel.
  const showFailed = isOnline && pending === 0 && failedCount > 0;
  const tone = !isOnline
    ? 'bg-destructive text-destructive-foreground'
    : showFailed
      ? 'bg-destructive/90 text-destructive-foreground'
      : 'bg-warning text-warning-foreground';

  return (
    <div className={`fixed top-0 inset-x-0 z-[9999] flex h-9 items-center justify-center gap-2 px-4 text-sm font-medium shadow-lg transition-all ${tone}`}>
      {!isOnline ? (
        <>
          <WifiOff className="h-4 w-4" />
          You are offline — POS sales will be queued and synced automatically when reconnected
          {pending + failedCount > 0 && <span className="ms-2 rounded-full bg-background/20 px-2 py-0.5 text-xs">{pending + failedCount} queued</span>}
        </>
      ) : showFailed ? (
        <>
          <AlertTriangle className="h-4 w-4" />
          {failedCount} offline sale{failedCount !== 1 ? 's' : ''} could not be synced
          <button onClick={showPanel} className="ms-2 rounded-md bg-background/20 px-2 py-0.5 text-xs font-semibold underline hover:bg-background/30">
            Review
          </button>
        </>
      ) : (
        <>
          <RefreshCw className="h-4 w-4 animate-spin" />
          Syncing {pending} offline sale(s)…
          {failedCount > 0 && (
            <button onClick={showPanel} className="ms-2 rounded-md bg-background/20 px-2 py-0.5 text-xs font-semibold underline hover:bg-background/30">
              {failedCount} failed — Review
            </button>
          )}
        </>
      )}
    </div>
  );
}
